import { t } from '@/features/i18n/utils';
import { SuspenseBoundary } from '@/ui/components/suspense-boundary';
import { insightsCountQuery } from '@/features/insights/queries';
import { activeTasksCountQuery } from '@/features/tasks/queries';
import { artifactsCountQuery } from '@/features/artifacts/queries';
import { ArtifactCountBadge } from '@/features/artifacts/components/artifact-count-badge';
import { InsightCountBadge } from '@/features/insights/components/insight-count-badge';
import { TaskCountBadge } from '@/features/tasks/components/task-count-badge';
import { TaskPanelHost } from '@/features/tasks/components/task-panel';
import { CreateTask } from '@/features/tasks/components/create-task';
import { createFileRoute, Link, notFound, Outlet, useMatches } from '@tanstack/react-router';
import { ChatProvider } from '@/features/chat/components/chat-provider';
import {
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarHeading,
  SidebarSection,
  SidebarSectionPanel,
  SidebarHelpButton,
  SidebarIndentedPanel,
  SidebarItem,
  SidebarLink,
  SidebarList,
  SidebarLogo,
  SidebarReleaseNotes,
  SidebarTrigger,
  SidebarUserMenu,
} from '@/ui/components/sidebar';
import { Icon } from '@/ui/components/icon';
import {
  ArrowLeft02Icon,
  Building06Icon,
  Bookmark01Icon,
  FolderLibraryIcon,
  InboxIcon,
  SparklesIcon,
  SettingsIcon,
  LibrariesIcon,
  SquareCheckBigIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import {
  BottomNavigation,
  BottomNavigationLink,
  BottomNavigationList,
} from '@/ui/components/bottom-navigation';
import { Spinner } from '@/ui/components/spinner';
import { AxiosError } from 'axios';
import { WorkspaceSidebarSectionContent } from '@/features/workspaces/components/workspace-sidebar-section-content';
import { projectDetailsQuery } from '@/features/projects/queries';
import { projectWorkspacesQuery, workspaceQuery } from '@/features/workspaces/queries';
import { WorkspaceTypeIcon } from '@/features/workspaces/components/workspace-type-icon';
import { unreadCountQuery } from '@/features/notifications/queries';
import { UnreadIndicator } from '@/features/comments/components/unread-indicator';
import { FavoritesList } from '@/features/favorites/components/favorites-list';
import { RecentWorkspaces } from '@/features/workspaces/components/recent-workspaces';
import { AccessRestrictedDialog } from '@/features/projects/components/access-restricted-dialog';
import { useParams } from '@tanstack/react-router';
import { WorkspaceBottomNavigationContent } from '@/features/workspaces/components/workspace-bottom-navigation-section-content';
import { Main } from '@/ui/components/main';
import { noop, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { ErrorComponent } from '@/ui/components/error';
import { FilePreviewSheet } from '@/features/documents/preview/components/file-preview-sheet';
import z from 'zod';
import { Skeleton } from '@/ui/components/skeleton';
import { CreateWorkspace } from '@/features/workspaces/create-workspace';
import { Frame, FramePanel } from '@/ui/components/frame';
import { ChatAnnouncementCard } from '@/features/chat/components/chat-announcement-card';
import { OrganizationSidebar } from '@/features/organizations/components/organization-sidebar';
import { GlobalSearch } from '@/features/search/components/global-search';
import { HoverScrollText } from '@/ui/components/hover-scroll-text';

export const Route = createFileRoute('/_authed/project/$projectId')({
  component: RouteComponent,
  loader: async (opts) => {
    opts.context.queryClient.query(projectWorkspacesQuery(opts.params.projectId)).catch(noop);
    if (!/\/workspaces\//.test(opts.location.pathname)) {
      const scope = {
        organizationId: opts.context.auth.user.organizationId,
        projectId: opts.params.projectId,
      };
      opts.context.queryClient
        .infiniteQuery(insightsCountQuery({ projectId: opts.params.projectId }))
        .catch(noop);
      opts.context.queryClient.infiniteQuery(activeTasksCountQuery(scope)).catch(noop);
      opts.context.queryClient.infiniteQuery(artifactsCountQuery(scope)).catch(noop);
    }
    const project = await opts.context.queryClient.query({
      ...projectDetailsQuery(opts.params.projectId),
      staleTime: 'static',
    });
    if (!project) throw notFound();
  },
  pendingComponent: () => (
    <div className="flex max-md:flex-col grow min-w-0 bg-background">
      <OrganizationSidebar />
      <Main className="relative">
        <Spinner kind="inset" />
      </Main>
    </div>
  ),
  errorComponent: (props) => {
    if (props.error instanceof AxiosError && props.error.response?.status === 403)
      return <AccessRestrictedDialog />;

    return <ErrorComponent {...props} />;
  },
  validateSearch: z.object({
    taskId: z.uuid().optional().catch(undefined),
    commentId: z.string().optional(),
    threadId: z.string().optional(),
    messageId: z.string().optional(),
    assessmentStatementId: z.string().optional(),
    commentThreadId: z.string().optional(),
  }),
});

function RouteComponent() {
  const matches = useMatches();
  const { workspaceId } = useParams({ strict: false });
  const { projectId } = Route.useParams();
  const { data: project } = useSuspenseQuery(projectDetailsQuery(projectId));

  const { data: unreadData } = useQuery(unreadCountQuery());

  const hasUnread = (unreadData?.unreadCount ?? 0) > 0;

  const { data: workspace } = useQuery({
    ...workspaceQuery(workspaceId ?? ''),
    enabled: !!workspaceId,
  });

  return (
    <div className="flex max-md:flex-col grow min-w-0 bg-background">
      <CreateWorkspace />
      <Sidebar>
        <SidebarContent>
          <SidebarHeader>
            <SidebarLogo />
            <GlobalSearch />
            <SidebarTrigger />
          </SidebarHeader>
          {workspaceId ? (
            <Frame className="rounded-xl border-surface-3 bg-surface-2 shadow-none">
              <Link
                className="flex text-xs mb-1.5 gap-2 items-center group/back-link"
                to="/project/$projectId"
                params={{ projectId }}
              >
                <div className="size-9 border group-hover/back-link:bg-white shrink-0 rounded-lg grid place-items-center">
                  <Icon
                    icon={ArrowLeft02Icon}
                    className="size-5 hidden group-hover/back-link:block"
                  />
                  <Icon icon={Building06Icon} className="size-5 group-hover/back-link:hidden" />
                </div>
                <div className="translate-y-px min-w-0">
                  <p className="text-muted text-xs font-medium group-hover/back-link:hidden">
                    {t({ en: 'Project', nb: 'Prosjekt' })}
                  </p>
                  <p className="text-muted text-xs font-medium hidden group-hover/back-link:block">
                    {t({ en: 'Back to project', nb: 'Tilbake til prosjekt' })}
                  </p>
                  <HoverScrollText className="text-sm font-medium">{project.name}</HoverScrollText>
                </div>
              </Link>
              <FramePanel className="rounded-lg! flex-row items-center gap-2 py-2 text-sm font-medium px-2.75 shadow-2xs">
                {workspace ? (
                  <>
                    <WorkspaceTypeIcon className="size-4 shrink-0" type={workspace.type} />
                    <HoverScrollText>{workspace.name}</HoverScrollText>
                  </>
                ) : (
                  <Skeleton className="w-full h-5" />
                )}
              </FramePanel>
            </Frame>
          ) : (
            <SidebarList>
              <SidebarItem>
                <SidebarLink to="/">
                  <Icon icon={Building06Icon} />
                  {t({ en: 'Projects', nb: 'Prosjekter' })}
                </SidebarLink>
              </SidebarItem>
              <SidebarItem>
                <SidebarLink to={'/notifications'} className="relative">
                  <Icon icon={InboxIcon} />
                  {hasUnread && <UnreadIndicator className="absolute top-1 left-4.5" />}
                  {t({ en: 'Notifications', nb: 'Notifikasjoner' })}
                </SidebarLink>
              </SidebarItem>
              <SidebarItem>
                <SidebarLink to={'/my-tasks'}>
                  <Icon icon={SquareCheckBigIcon} />
                  {t({ en: 'My tasks', nb: 'Mine oppgaver' })}
                </SidebarLink>
              </SidebarItem>
              <SidebarItem>
                <SidebarLink to={'/learnings'}>
                  <Icon icon={SparklesIcon} />
                  {t({ en: 'Learnings', nb: 'Erfaringer' })}
                </SidebarLink>
              </SidebarItem>
              <SidebarItem>
                <SidebarLink to={'/settings/profile'}>
                  <Icon icon={SettingsIcon} />
                  {t({ en: 'Settings', nb: 'Innstillinger' })}
                </SidebarLink>
              </SidebarItem>
            </SidebarList>
          )}
          {workspaceId ? (
            workspace && <WorkspaceSidebarSectionContent />
          ) : (
            <SidebarSection id="your-projects">
              <SidebarHeading>{t({ en: 'Your project', nb: 'Dine prosjekt' })}</SidebarHeading>
              <SidebarSectionPanel className="space-y-1">
                <SidebarLink
                  className="group/accordion-trigger-link"
                  to="/project/$projectId"
                  params={{ projectId }}
                  data-inactive={
                    matches.some(
                      (m) =>
                        m.routeId === '/_authed/project/$projectId/' ||
                        m.routeId === '/_authed/project/$projectId/members' ||
                        m.routeId === '/_authed/project/$projectId/settings',
                    )
                      ? undefined
                      : ''
                  }
                >
                  <Icon icon={Building06Icon} />
                  <HoverScrollText>{project?.name}</HoverScrollText>
                </SidebarLink>
                <SidebarIndentedPanel>
                  <SidebarList>
                    <SidebarLink to="/project/$projectId/artifacts" params={{ projectId }}>
                      <Icon icon={LibrariesIcon} />
                      <HoverScrollText>
                        {t({ en: 'Working documents', nb: 'Arbeidsdokumenter' })}
                      </HoverScrollText>
                      <SuspenseBoundary>
                        <ArtifactCountBadge />
                      </SuspenseBoundary>
                    </SidebarLink>
                    <SidebarLink to="/project/$projectId/insights" params={{ projectId }}>
                      <Icon icon={Bookmark01Icon} />
                      {t({ en: 'Saved findings', nb: 'Lagrede funn' })}
                      <SuspenseBoundary>
                        <InsightCountBadge />
                      </SuspenseBoundary>
                    </SidebarLink>
                    <SidebarLink to="/project/$projectId/tasks" params={{ projectId }}>
                      <Icon icon={SquareCheckBigIcon} />
                      {t({ en: 'Tasks', nb: 'Oppgaver' })}
                      <SuspenseBoundary>
                        <TaskCountBadge />
                      </SuspenseBoundary>
                    </SidebarLink>
                  </SidebarList>
                </SidebarIndentedPanel>
              </SidebarSectionPanel>
            </SidebarSection>
          )}
          <RecentWorkspaces />
          <FavoritesList />
          <div className="mt-auto space-y-3">
            <ChatAnnouncementCard />
            <SidebarList>
              <SidebarHelpButton />
              <SidebarReleaseNotes />
            </SidebarList>
            <SidebarUserMenu />
          </div>
        </SidebarContent>
      </Sidebar>
      {workspaceId ? (
        <Outlet />
      ) : (
        <ChatProvider key={projectId}>
          <Outlet />
        </ChatProvider>
      )}
      <TaskPanelHost />
      <SuspenseBoundary>
        <CreateTask />
      </SuspenseBoundary>
      <FilePreviewSheet />
      <BottomNavigation>
        <BottomNavigationList>
          {workspaceId ? (
            workspace && <WorkspaceBottomNavigationContent />
          ) : (
            <>
              <BottomNavigationLink
                to="/project/$projectId"
                data-inactive={
                  matches.some((m) => m.routeId === '/_authed/project/$projectId/') ? undefined : ''
                }
                params={{ projectId }}
              >
                <Icon icon={FolderLibraryIcon} />
                {t({ en: 'Workspaces', nb: 'Arbeidsområder' })}
              </BottomNavigationLink>
              <BottomNavigationLink to="/project/$projectId/insights">
                <Icon icon={Bookmark01Icon} />
                {t({ en: 'Saved findings', nb: 'Lagrede funn' })}
              </BottomNavigationLink>
              <BottomNavigationLink to="/project/$projectId/tasks" params={{ projectId }}>
                <Icon icon={SquareCheckBigIcon} />
                {t({ en: 'Tasks', nb: 'Oppgaver' })}
              </BottomNavigationLink>
            </>
          )}
        </BottomNavigationList>
      </BottomNavigation>
    </div>
  );
}
